import { useState, useEffect} from 'react';
import { useIamUserProfileStore } from '../../Store';
import getCdkSidFromOkta from '../../util/getCdkSidFromOkta';
import { SPOKE_USERS } from '../../constants';
import {
  useGetIPRestriction,
  useSaveIPRestriction,
  useGetIPTemplate
} from './useDealerAdminQueries';
import { BaseSideSheet } from './BaseSideSheet';
import { AdminDataSideSheetHeader } from '../UnifyAdministration/Blocks/AdminDataSideSheetHeader';
import { IPRestrictionSideSheetBody } from './IPRestrictionSideSheetBody';
import {
  IPRestrictionUIModel,
  IPRestrictionData
} from './IPRestrictionInterface';
import { SkeletonLoader } from 'cdk-radial';
import IPRestrictionGrid from './IPRestrictionGrid';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import useAuthToken from '../../hooks/useAuthToken';
import * as Api from '../../util/api';

export const AdminTableBodySkeleton = (): JSX.Element => {
  return (
    <div>
      {[...Array(20)].map((item, key) => (
        <SkeletonLoader
          key={key}
          dataTestId={`loader-${key}`}
          style={{
            margin: '1.5rem 0',
            height: '20px',
          }}
          variant="text"
        ></SkeletonLoader>
      ))}
    </div>
  );
};

const IPRestrictionGridWrapper = (({ rowData, onSelectRow ,gridRef}: any) => {
  return <IPRestrictionGrid rowData={rowData} onSelectRow={onSelectRow} gridRef={gridRef}/>;
});

export default function IPRestrictionContainer({
  setToast,
  isTemplateOpen,
  gridRef,
  storeId,
  storeName
}:any): JSX.Element {

  const [gridData, setGridData] = useState<IPRestrictionData[]>();
  const iamUserInfo = useIamUserProfileStore((state: any) => state.iamUserInfo);
  const cdksid = getCdkSidFromOkta();
  const {t} = useTranslation();
  const { trackEvent } = useAnalytics();

  const [isSideSheetCollapse, setSideSheetCollapse] = useState(true);
  const [sideSheetData, setSideSheetData] = useState<IPRestrictionUIModel>();
  
  useEffect(() => {
    trackEvent(AMPLITUDE_EVENTS.DEALER_ADMINISTRATION, { Action: 'View IP Access' });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if(isTemplateOpen){
      setSideSheetCollapse(!!isTemplateOpen)
    }
  }, [isTemplateOpen]);

  useEffect(() => {
    setSideSheetCollapse(true);
    setGridData(undefined);
  }, [storeId]);

  const enterpriseId = iamUserInfo?.fullUser
    ? iamUserInfo?.fullUser?.enterprise?.id
    : !cdksid
    ? SPOKE_USERS
    : null;

  const { data: rowData } = useGetIPRestriction(
    enterpriseId,
    storeId
  );

  const { data: templateData } = useGetIPTemplate(
    enterpriseId,
    storeId
  );

  const { mutation: saveMutation } = useSaveIPRestriction({
    enterpriseId,
    storeId,
    onSuccess: () => {
      setToast(t('dealer-admin.ip-access.label.savedMsg'),'Positive',true)
      setSideSheetCollapse(true);
    },
    onError: () => {
      setToast(t('dealer-admin.ip-access.label.failedMsg'),'Error',true)
      setSideSheetCollapse(true);
    },
  });

  const onHandleSelectRow = (row: IPRestrictionData) => {

    setSideSheetCollapse(true);
    setToast('','',false);

    let name = "";
    if (row.type === 'application' ){
      name = row.id === 'UNIFY' ? "Unify" : row.name[1];
    }else{
      name = row.name[2];
    }

    const selectedModel: IPRestrictionUIModel = {
      entityId: row.id,
      entityName: name,
      allowedIps: row.allowedIps,
      allowedIpRange: row.allowedIpRange,
      entityType: row.type,
      storeName: storeName,
      storeId: storeId,
      iconColor:row.iconColor,
      iconText:row.iconText,
      message:row.message,
      availableTemplates:templateData?.templates,
      templates: row.templates
    };

    setSideSheetData(selectedModel);
    setSideSheetCollapse(false);
    
  };

  const token = useAuthToken();

  const onSave = async (model: any) => {
    if (model.storeId && model.storeId !== storeId) {
      try {
        await Api.saveIPRestriction(token, enterpriseId, model.storeId, model);
      } catch (e) {
        console.error('Failed to save IP restriction for store:', model.storeId, e);
      }
    } else {
      saveMutation.mutate(model);
    }
  };

  const onCancel = () => {
    setSideSheetCollapse(true);
  };
  

  useEffect(() => {
    if(rowData){
      const updated = rowData?.map(item => ({
        ...item,
        availableTemplates: templateData?.templates || []
      }));
      setGridData(updated as IPRestrictionData[]);
    }
  }, [rowData,templateData]);

  return (
    <>

      {!gridData ? (
        <AdminTableBodySkeleton></AdminTableBodySkeleton>
      ) : (
        <IPRestrictionGridWrapper
          gridRef={gridRef}
          rowData={gridData}
          onSelectRow={onHandleSelectRow}
        ></IPRestrictionGridWrapper>
      )}

      {!isSideSheetCollapse && (
        <BaseSideSheet isCollapse={isSideSheetCollapse}>
          <AdminDataSideSheetHeader
            sideSheetHeaderTitle={t('dealer-admin.ip-access.detail.title')}
            handleSideSheetClose={() => setSideSheetCollapse(true)}
          />
          {sideSheetData && (
            <IPRestrictionSideSheetBody
              model={sideSheetData}
              onSave={onSave}
              onCancel={onCancel}
            />
          )}
        </BaseSideSheet>
      )}

    </>
  );
}